/**
 * Octopus IoT Platform - Equipment Geofence Config
 * FASE 2: Component for configuring geofence rules per equipment
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { 
  MapPin, 
  Plus, 
  Trash2, 
  LogIn, 
  LogOut, 
  AlertTriangle,
  Loader2,
  Save,
  RefreshCw,
  Hexagon
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { useTimezone } from '@/contexts/TimezoneContext';
import { geofenceApi } from '@/services/geofenceApi';
import GeofenceRuleEditor, { SEVERITY_OPTIONS } from './GeofenceRuleEditor';
import GeofenceMap from './GeofenceMap';
import { validPhoneList } from '@/components/PhoneNumberInput';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

// Event type icons and colors
const EVENT_ICONS = {
  'exit': { icon: LogOut, color: 'text-red-500', label: 'Saiu' },
  'enter': { icon: LogIn, color: 'text-green-500', label: 'Entrou' },
  'approaching_exit': { icon: AlertTriangle, color: 'text-yellow-500', label: 'Aproximou (saída)' },
  'approaching_enter': { icon: AlertTriangle, color: 'text-blue-500', label: 'Aproximou (entrada)' },
};

const sanitizeRulePhones = (rules = []) => rules.map((rule) => ({
  ...rule,
  notification_phones: validPhoneList(rule.notification_phones || []),
}));

/**
 * EquipmentGeofenceConfig Component
 * 
 * @param {string} equipmentId - Equipment UUID
 * @param {string} equipmentName - Equipment name for display
 * @param {boolean} isOpen - Whether modal is open
 * @param {function} onClose - Callback to close modal
 */
const EquipmentGeofenceConfig = ({ equipmentId, equipmentName, isOpen, onClose }) => {
  // Get user role from auth context
  const { user } = useAuth();
  const { formatDateTime } = useTimezone();
  const userRole = user?.role || 'user';
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rules, setRules] = useState([]);
  const [availableGeofences, setAvailableGeofences] = useState([]);
  const [events, setEvents] = useState([]);
  const [status, setStatus] = useState(null);
  const [selectedGeofenceId, setSelectedGeofenceId] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [rulesRes, geofencesRes, eventsRes, statusRes] = await Promise.all([
        geofenceApi.getEquipmentRules(equipmentId),
        geofenceApi.listGeofences(false),
        geofenceApi.getEquipmentEvents(equipmentId, 20),
        geofenceApi.getEquipmentGeofenceStatus(equipmentId)
      ]);
      
      setRules(rulesRes.data.rules || []);
      setAvailableGeofences(geofencesRes.data.geofences || []);
      setEvents(eventsRes.data.events || []);
      setStatus(statusRes.data);
    } catch (error) {
      console.error('Error loading geofence data:', error);
      toast.error('Erro ao carregar dados de geofence');
    } finally {
      setLoading(false);
    }
  }, [equipmentId]);

  // Load data
  useEffect(() => {
    if (equipmentId) {
      loadData();
    }
  }, [equipmentId, loadData]);
  
  const availablePolygonGeofences = availableGeofences.filter(
    (gf) => (gf.geometry_type || (gf.point ? 'waypoint' : 'polygon')) === 'polygon'
  );

  // Get polygon geofences that are not yet linked
  const unlinkedGeofences = availablePolygonGeofences.filter(
    gf => !rules.some(r => r.geofence_id === gf.id)
  );
  
  // Add new geofence link
  const handleAddGeofence = async () => {
    if (!selectedGeofenceId) return;
    
    setSaving(true);
    try {
      const defaultRule = {
        trigger: 'on_exit',
        severity: 'medium',
        repeat_interval_minutes: 5,
        notify_email: false,
        notify_whatsapp: false,
        notification_emails: [],
        notification_phones: []
      };
      
      await geofenceApi.createEquipmentRule(equipmentId, {
        geofence_id: selectedGeofenceId,
        enabled: true,
        rules: [defaultRule]
      });
      
      toast.success('Geofence vinculada');
      setSelectedGeofenceId('');
      loadData();
    } catch (error) {
      console.error('Error adding geofence:', error);
      const msg = error.response?.data?.detail || 'Erro ao vincular geofence';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };
  
  // Toggle rule enabled/disabled
  const handleToggleRule = async (rule) => {
    try {
      await geofenceApi.updateEquipmentRule(equipmentId, rule.id, {
        enabled: !rule.enabled
      });
      
      setRules(prev => prev.map(r => 
        r.id === rule.id ? { ...r, enabled: !r.enabled } : r
      ));
      
      toast.success(rule.enabled ? 'Regra desativada' : 'Regra ativada');
    } catch (error) {
      console.error('Error toggling rule:', error);
      toast.error('Erro ao atualizar regra');
    }
  };
  
  // Update rule triggers
  const handleUpdateRuleTriggers = async (ruleId, newRules) => {
    setSaving(true);
    try {
      await geofenceApi.updateEquipmentRule(equipmentId, ruleId, {
        rules: sanitizeRulePhones(newRules)
      });
      
      setRules(prev => prev.map(r => 
        r.id === ruleId ? { ...r, rules: newRules } : r
      ));
      
      toast.success('Regra atualizada');
    } catch (error) {
      console.error('Error updating rule:', error);
      toast.error('Erro ao atualizar regra');
    } finally {
      setSaving(false);
    }
  };
  
  // Add trigger to rule
  const handleAddTrigger = (ruleId) => {
    const rule = rules.find(r => r.id === ruleId);
    if (!rule) return;
    
    const newTrigger = {
      trigger: 'on_enter',
      severity: 'medium',
      repeat_interval_minutes: 5,
      notify_email: false,
      notify_whatsapp: false,
      notification_emails: [],
      notification_phones: []
    };
    
    const newRules = [...(rule.rules || []), newTrigger];
    handleUpdateRuleTriggers(ruleId, newRules);
  };
  
  // Update single trigger
  const handleTriggerChange = (ruleId, triggerIndex, updatedTrigger) => {
    const rule = rules.find(r => r.id === ruleId);
    if (!rule) return;
    
    const newRules = [...rule.rules];
    newRules[triggerIndex] = updatedTrigger;
    
    // Update local state immediately
    setRules(prev => prev.map(r => 
      r.id === ruleId ? { ...r, rules: newRules } : r
    ));
  };
  
  // Save trigger changes (debounced)
  const handleTriggerSave = (ruleId) => {
    const rule = rules.find(r => r.id === ruleId);
    if (rule) {
      handleUpdateRuleTriggers(ruleId, rule.rules);
    }
  };
  
  // Delete trigger from rule
  const handleDeleteTrigger = (ruleId, triggerIndex) => {
    const rule = rules.find(r => r.id === ruleId);
    if (!rule) return;
    
    const newRules = rule.rules.filter((_, i) => i !== triggerIndex);
    
    if (newRules.length === 0) {
      // If no triggers left, ask to delete entire rule
      setDeleteConfirm({ ruleId, geofenceName: rule.geofence_name });
    } else {
      handleUpdateRuleTriggers(ruleId, newRules);
    }
  };
  
  // Delete entire rule
  const handleDeleteRule = async () => {
    if (!deleteConfirm) return;
    
    try {
      await geofenceApi.deleteEquipmentRule(equipmentId, deleteConfirm.ruleId);
      toast.success('Vínculo removido');
      setDeleteConfirm(null);
      loadData();
    } catch (error) {
      console.error('Error deleting rule:', error);
      toast.error('Erro ao remover vínculo');
    }
  };
  
  if (loading) {
    return (
      <Dialog open={isOpen} onOpenChange={(open) => !open && onClose?.()}>
        <DialogContent className="sm:max-w-[1100px] max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Hexagon className="h-5 w-5 text-cyan-500" />
              Geofences - {equipmentName}
            </DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }
  
  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose?.()}>
      <DialogContent className="sm:max-w-[1100px] max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Hexagon className="h-5 w-5 text-cyan-500" />
            Geofences - {equipmentName}
          </DialogTitle>
          <DialogDescription>
            Configure cercas geográficas e regras de alerta para este equipamento
          </DialogDescription>
        </DialogHeader>
        
        <div className="grid grid-cols-5 gap-4 max-h-[calc(90vh-180px)]" data-testid="equipment-geofence-config">
          {/* COLUNA ESQUERDA — 3/5 do espaço */}
          <ScrollArea className="col-span-3 pr-4 max-h-[calc(90vh-180px)]">
            <div className="space-y-4">
      {/* Current Status */}
      {status && status.current_zones?.length > 0 && (
        <Card className="border-green-500/30 bg-green-500/5">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <MapPin className="h-4 w-4 text-green-500" />
              Localização Atual
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {status.current_zones.map((zone) => (
                <Badge 
                  key={zone.geofence_id} 
                  style={{ backgroundColor: zone.color }}
                  className="text-white"
                >
                  {zone.geofence_name}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Add Geofence */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Vincular Geofence</CardTitle>
          <CardDescription>
            Selecione um polígono para monitorar este equipamento
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2">
            <Select value={selectedGeofenceId} onValueChange={setSelectedGeofenceId}>
              <SelectTrigger className="flex-1" data-testid="geofence-select">
                <SelectValue placeholder="Selecione uma geofence" />
              </SelectTrigger>
              <SelectContent>
                {unlinkedGeofences.length === 0 ? (
                  <div className="p-2 text-sm text-muted-foreground text-center">
                    Todos os polígonos já estão vinculados
                  </div>
                ) : (
                  unlinkedGeofences.map((gf) => (
                    <SelectItem key={gf.id} value={gf.id}>
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: gf.color }}
                        />
                        {gf.name}
                      </div>
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
            <Button 
              onClick={handleAddGeofence}
              disabled={!selectedGeofenceId || saving}
              data-testid="add-geofence-btn"
            >
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Plus className="h-4 w-4" />
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
      
      {/* Linked Geofences Rules */}
      {rules.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm">Regras de Geofence</CardTitle>
              <Button variant="ghost" size="icon" onClick={loadData}>
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {rules.map((rule) => (
              <div 
                key={rule.id} 
                className="p-4 border rounded-lg space-y-3"
                data-testid={`geofence-rule-${rule.id}`}
              >
                {/* Rule Header */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div 
                      className="w-4 h-4 rounded-full"
                      style={{ backgroundColor: availableGeofences.find(g => g.id === rule.geofence_id)?.color || '#666' }}
                    />
                    <span className="font-medium">{rule.geofence_name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={rule.enabled}
                      onCheckedChange={() => handleToggleRule(rule)}
                      data-testid={`rule-toggle-${rule.id}`}
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-destructive hover:bg-destructive/10"
                      onClick={() => setDeleteConfirm({ ruleId: rule.id, geofenceName: rule.geofence_name })}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                
                {/* Triggers */}
                <div className="space-y-2">
                  {(rule.rules || []).map((trigger, idx) => (
                    <GeofenceRuleEditor
                      key={`${rule.id}-${idx}`}
                      rule={trigger}
                      onChange={(updated) => handleTriggerChange(rule.id, idx, updated)}
                      onDelete={() => handleDeleteTrigger(rule.id, idx)}
                      compact={true}
                      userRole={userRole}
                    />
                  ))}
                </div>
                
                {/* Add Trigger & Save */}
                <div className="flex gap-2 pt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleAddTrigger(rule.id)}
                    className="flex-1"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Adicionar Gatilho
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => handleTriggerSave(rule.id)}
                    disabled={saving}
                  >
                    <Save className="h-4 w-4 mr-1" />
                    Salvar
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
      
      {/* Recent Events */}
      {events.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Eventos Recentes</CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[150px]">
              <div className="space-y-2">
                {events.map((event) => {
                  const eventConfig = EVENT_ICONS[event.event_type] || EVENT_ICONS['exit'];
                  const EventIcon = eventConfig.icon;
                  
                  return (
                    <div 
                      key={event.id}
                      className="flex items-center gap-3 p-2 bg-muted/30 rounded"
                    >
                      <EventIcon className={cn("h-4 w-4", eventConfig.color)} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm truncate">
                          <span className={eventConfig.color}>{eventConfig.label}</span>
                          {' '}
                          <span className="text-muted-foreground">da zona</span>
                          {' '}
                          <span className="font-medium">{event.geofence_name}</span>
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {formatDistanceToNow(new Date(event.timestamp), { 
                            addSuffix: true,
                            locale: ptBR
                          })} • {formatDateTime(event.timestamp, { omitSeconds: true })}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      )}
      
      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={(open) => !open && setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remover vínculo?</AlertDialogTitle>
            <AlertDialogDescription>
              Isso removerá todas as regras de alerta para a geofence "{deleteConfirm?.geofenceName}".
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteRule} className="bg-destructive hover:bg-destructive/90">
              Remover
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
            </div>
          </ScrollArea>
          
          {/* COLUNA DIREITA — 2/5 do espaço */}
          <div className="col-span-2 flex flex-col">
            {/* Mapa das Geofences */}
            {rules.length > 0 ? (
              <Card className="flex-1 flex flex-col">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Mapa das Geofences</CardTitle>
                </CardHeader>
                <CardContent className="flex-1 pb-4">
                  <GeofenceMap
                    geofences={availableGeofences.filter(gf => 
                      rules.some(r => r.geofence_id === gf.id)
                    )}
                    height="100%"
                    className="min-h-[300px]"
                    interactive={false}
                  />
                </CardContent>
              </Card>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground border border-dashed rounded-lg">
                <Hexagon className="h-12 w-12 mb-2 opacity-30" />
                <p className="text-sm">Vincule uma geofence</p>
                <p className="text-xs">para ver no mapa</p>
              </div>
            )}
          </div>
        </div>
        
        {/* Footer com botão Fechar */}
        <div className="flex justify-end pt-4 border-t border-border mt-4">
          <Button onClick={onClose} data-testid="geofence-close-btn">
            Fechar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EquipmentGeofenceConfig;
